import React from 'react';
import { Link } from 'react-router-dom';

const SAFE_HREF = /^(\/(?!\/)|https?:\/\/|mailto:|tel:)/i;
const INLINE_RE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\)|`[^`]+`)/g;

const renderInline = (text, keyBase, onNavigate) =>
  text.split(INLINE_RE).filter(Boolean).map((part, i) => {
    const key = `${keyBase}-${i}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key} className="font-semibold text-[#1e2d3b]">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={key} className="px-1 py-0.5 rounded bg-slate-100 text-[0.85em]">{part.slice(1, -1)}</code>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const [, label, href] = link;
      if (!SAFE_HREF.test(href)) return <span key={key}>{label}</span>;
      const cls = 'text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-orange-600 hover:decoration-orange-300 transition-colors';
      if (href.startsWith('/')) {
        return <Link key={key} to={href} onClick={onNavigate} className={cls} data-testid="charlie-answer-link">{label}</Link>;
      }
      return <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid="charlie-answer-link">{label}</a>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });

/** Tiny, safe markdown renderer: paragraphs, bullets, numbered lists, bold, links. */
export const ChatMarkdown = ({ text, onNavigate }) => {
  const blocks = [];
  let list = null;
  const flushList = () => { if (list) { blocks.push(list); list = null; } };

  text.split('\n').forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flushList(); return; }
    const bullet = line.match(/^[-*•]\s+(.*)/);
    const numbered = line.match(/^(\d+)[.)]\s+(.*)/);
    if (bullet || numbered) {
      const type = bullet ? 'ul' : 'ol';
      if (!list || list.type !== type) { flushList(); list = { type, items: [] }; }
      list.items.push({ text: (bullet || numbered)[bullet ? 1 : 2], idx });
      return;
    }
    flushList();
    const heading = line.match(/^#{1,6}\s+(.*)/);
    blocks.push({ type: heading ? 'h' : 'p', text: heading ? heading[1] : line, idx });
  });
  flushList();

  return (
    <div className="space-y-2 leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === 'ul' || b.type === 'ol') {
          const Tag = b.type;
          return (
            <Tag key={`${b.type}-${b.items[0]?.idx ?? i}`} className={`${b.type === 'ul' ? 'list-disc' : 'list-decimal'} pl-5 space-y-1 marker:text-orange-500`}>
              {b.items.map((it) => <li key={it.idx}>{renderInline(it.text, `l${it.idx}`, onNavigate)}</li>)}
            </Tag>
          );
        }
        if (b.type === 'h') return <p key={`h-${b.idx}`} className="font-semibold text-[#1e2d3b]">{renderInline(b.text, `h${b.idx}`, onNavigate)}</p>;
        return <p key={`p-${b.idx}`}>{renderInline(b.text, `p${b.idx}`, onNavigate)}</p>;
      })}
    </div>
  );
};
